import { useMemo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getWhatsappTemplateVariables } from '@frontend/features/whatsapp/constants/templateVariables';

export const WhatsappQuickReplyVariablesHelp = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const placeholders = useMemo(
    () => getWhatsappTemplateVariables(t),
    [t],
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 960,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="whatsapp-quick-reply-variables-content"
        id="whatsapp-quick-reply-variables-header"
        sx={{
          px: 2,
          py: 0.5,
          '& .MuiAccordionSummary-content': {
            my: 1,
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {t('whatsapp.settings.quickReplies.help.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('whatsapp.settings.quickReplies.help.intro')}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="caption" color="text.secondary">
            {t('whatsapp.settings.quickReplies.help.note')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={1}>
            {placeholders.map((placeholder) => (
              <Box
                key={placeholder.token}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1.5}
              >
                <Typography
                  component="code"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {placeholder.token}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {placeholder.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box display="flex" flexDirection="column" gap={0.75}>
            <Typography variant="caption" color="text.secondary">
              {t('whatsapp.settings.quickReplies.help.exampleLabel')}
            </Typography>
            <Typography
              component="code"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                px: 1.5,
                py: 1,
                borderRadius: 1,
                backgroundColor: 'grey.100',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                whiteSpace: 'pre-wrap',
              }}
            >
              {t('whatsapp.settings.quickReplies.help.exampleValue')}
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
